import { getCurrentDateFormat, type DateType, type TimeType } from '@/utilities/dateutils';
import { Backspace, Delete } from '@/utilities/keyboardutils';
import type IInputMaskItem from './IInputMaskItem';
import InputMaskNumberRange from './InputMaskNumberRange';
import InputMaskSeparator from './InputMaskSeparator';
import InputMaskPlusMinus from './InputMaskPlusMinus';
import { MaskValue } from './IInputMaskItem';

export default class InputMaskData {
    private MaskItems: IInputMaskItem[] = [];

    private maskItemCaretIndexMap: IInputMaskItem[] = [];

    public static CreateFromPhoneMask(mask: string): InputMaskData {
        const maskData = new InputMaskData();

        // Iterate each character in the mask and add the corresponding maskItem
        for (let i = 0; i < mask.length; i++) {
            const char = mask[i];
            if (char == '#') {
                maskData.addMaskItem(new InputMaskNumberRange('#', 0, 9));
            } else {
                maskData.addMaskItem(new InputMaskSeparator(char, false));
            }
        }

        return maskData;
    }

    public static CreateFromTimeSpan(dateFormatType: TimeType): InputMaskData {
        const dateFormat = getCurrentDateFormat(dateFormatType);
        const maskData = new InputMaskData();

        const patternsLookup = {
            hh: { min: 0, max: 99 },
            mm: { min: 0, max: 59 },
            ss: { min: 0, max: 59 },
            fff: { min: 0, max: 999 },
            ff: { min: 0, max: 99 },
            f: { min: 0, max: 9 },
        };

        const patterns = Object.keys(patternsLookup);

        let separator = null;

        // We always add a item for the +/- sign
        maskData.addMaskItem(new InputMaskPlusMinus());

        for (let i = 0; i < dateFormat.length; i++) {
            const pattern = patterns.find((p) => p == dateFormat.slice(i, i + p.length));

            // If we dont have a pattern, add to separator
            if (!pattern) {
                if (!separator) {
                    separator = new InputMaskSeparator('');
                    maskData.addMaskItem(separator);
                }
                separator.mask += dateFormat[i];
                continue;
            }

            // Reset separator for next iteration
            separator = null;

            const { min, max } = patternsLookup[pattern as keyof typeof patternsLookup];
            maskData.addMaskItem(new InputMaskNumberRange(pattern, min, max));
            i += pattern.length - 1;
        }

        return maskData;
    }

    public static CreateFromDateFormat(dateFormatType: DateType): InputMaskData {
        const dateFormat = getCurrentDateFormat(dateFormatType);
        const maskData = new InputMaskData();

        const patternsLookup = {
            yyyy: { min: 0, max: 9999 },
            MM: { min: 1, max: 12 },
            dd: { min: 0, max: 31 },
            hh: { min: 0, max: 23 },
            mm: { min: 0, max: 59 },
            ss: { min: 0, max: 59 },
            fff: { min: 0, max: 999 },
            ff: { min: 0, max: 99 },
            f: { min: 0, max: 9 },
        };

        const patterns = Object.keys(patternsLookup);

        let separator = null;

        for (let i = 0; i < dateFormat.length; i++) {
            const pattern = patterns.find((p) => p == dateFormat.slice(i, i + p.length));

            // If we dont have a pattern, add to separator
            if (!pattern) {
                if (!separator) {
                    separator = new InputMaskSeparator('');
                    maskData.addMaskItem(separator);
                }
                separator.mask += dateFormat[i];
                continue;
            }

            // Reset separator for next iteration
            separator = null;

            const { min, max } = patternsLookup[pattern as keyof typeof patternsLookup];
            maskData.addMaskItem(new InputMaskNumberRange(pattern, min, max));
            i += pattern.length - 1;
        }

        return maskData;
    }

    addMaskItem(maskItem: IInputMaskItem) {
        this.MaskItems.push(maskItem);
    }

    /**
     * Clears user input
     */
    clear() {
        this.MaskItems.forEach((mi) => mi.clear());
    }

    /**
     * This sets the value in the maskinput and matches that the input string has legal values for each mask value.
     * This can be used to set a partially filled mask, but the string needs to match the mask exactly.
     * Example for a datemask 'dd.MM.yyyy hh:mm:ss' this will support 'dd.12.202y xx:12:1s'
     * @param value The value we want to set for the mask, if empty it will clear user input
     */
    setStringValue(value: string) {
        if (!value?.length) {
            this.clear();
            return;
        }
        this.setStringValueAtPosition(value, 0);
    }

    setStringValueAtPosition(value: string, startPosition: number): number {
        let newCaretIndex = 0;
        Array.from(value, (c, i) => {
            newCaretIndex = this.handleInput(c, i == 0 ? i + startPosition : newCaretIndex);
        });
        return newCaretIndex;
    }

    /**
     * Handles input of one character
     * @param char the character to input. This is just one character even if the type is string
     * @param caretIndex the caret index where the character should be inserted
     * @returns the new caret index
     */
    handleInput(char: string, caretIndex: number): number {
        // Make sure we have a map of every index of the mask to the corresponding maskItem
        this.createMaskItemCaretIndexMap();

        // Handle Backspace
        if (char == Backspace) {
            return this.handleBackSpace(caretIndex);
        }

        // If we are the very end of the mask we wont accept any input
        if (caretIndex == this.maskItemCaretIndexMap.length) {
            return caretIndex;
        }

        // Handle delete
        if (char == Delete) {
            this.handleDelete(caretIndex);
            return caretIndex;
        }

        // Handle normal characters
        return this.handleCharInput(char, caretIndex);
    }

    /**
     * Returns userinput + mask
     * This is the actual value that the InputMask emits
     * So if the mask is yyyy and user as put 19, this will return 19yy
     * If the mask is a separator and it should not be included in the output it will be skipped.
     */
    getCurrentDisplayText(): string {
        return this.MaskItems.reduce((res, mi) => res + mi.getCurrentDisplayValue(), '');
    }

    /**
     * Returns userinput with spaces for missing mask values
     * This is the text that the InputMask uses in its input element
     * So if the mask is yyyy and user as put 19, this will return '19  '
     */
    getCurrentTextForInputElement(): string {
        return this.MaskItems.reduce((res, mi) => res + mi.getCurrentTextForInputElement(this.isMaskFilled()), '');
    }

    /**
     * Returns the gray mask string
     * Each character will be the placeholder if not filled, otherwise it will be the userfilled value.
     * This will include separators even if they are marked as skipInOutput
     * @returns
     */
    getGrayMaskValue(): string {
        return this.MaskItems.reduce((res, mi) => res + mi.getGrayMaskValue(), '');
    }

    /**
     * Checks if the entire mask is filled, for all maskItems
     * @returns True if the mask is filled, false otherwise
     */
    isMaskFilled(): boolean {
        return this.MaskItems.every((mi) => mi.isMaskFilled());
    }

    /**
     * Finds the first index in the mask that doesnt have a value filled
     * If all values are filled then this returns the last index of the mask
     */
    getFirstUnfilledIndex(): number {
        this.createMaskItemCaretIndexMap();
        for (let i = 0; i < this.maskItemCaretIndexMap.length; i++) {
            const mi = this.maskItemCaretIndexMap[i];
            if (mi.isMaskFilled()) {
                continue;
            }

            return i + mi.findFirstUnfilledIndex();
        }
        return this.maskItemCaretIndexMap.length;
    }

    /**
     * Gets the current value for a part of the mask
     *
     * Example:
     *
     * Mask: dd.MM.yyyy
     *
     * Value: 0d.MM.19yy
     *
     * mask = 'dd' gives '0d'
     *
     * mask = 'MM' gives 'MM'
     *
     * mask = 'yyyy' gives '19yy'
     *
     * mask = 'none' gives 'none'
     * @param mask The mask we want to find the value for
     * @returns The current displayValue for the found maskItem or the mask input parameter if no matching maskItem is found
     */
    getCurrentStringForMask(mask: string): string | undefined {
        const maskItem = this.MaskItems.find((m) => m.mask == mask);
        if (!maskItem) {
            return undefined;
        }
        return maskItem.getCurrentDisplayValue();
    }

    private createMaskItemCaretIndexMap() {
        if (this.maskItemCaretIndexMap.length) {
            return;
        }

        let maskIndex = 0;

        for (const maskItem of this.MaskItems) {
            for (let i = maskIndex; i < maskIndex + maskItem.mask.length; i++) {
                this.maskItemCaretIndexMap[i] = maskItem;
            }

            maskIndex += maskItem.mask.length;
        }
    }

    private getMaskItemForCaretIndex(caretIndex: number) {
        if (caretIndex < 0 || caretIndex > this.maskItemCaretIndexMap.length) {
            throw new Error(
                `Invalid caretIndex when trying to find maskItem. CaretIndex: ${caretIndex}, maskLength: ${
                    this.maskItemCaretIndexMap.length
                }, mask: ${this.getFullMask()}`,
            );
        }

        return this.maskItemCaretIndexMap[caretIndex];
    }

    /** Turns a caretIndex into the relative index for a given maskItem
     *  So if the caretindex is 4 and we have 2 maskitems with each 2 characters the relative index will be 1 for the second maskitem
     */
    private getRelativeCharIndex(caretIndex: number, maskItem: IInputMaskItem) {
        // Start counting at caretIndex until we reach another maskItem than the one we are checking for. The relative index will be the number of steps
        let relative = 0;
        for (let i = caretIndex; i > 0; i--) {
            // If the previous character is a different maskItem we have gotten to the end
            if (this.maskItemCaretIndexMap[i - 1] !== maskItem) {
                break;
            }
            relative++;
        }
        return relative;
    }

    private handleBackSpace(caretIndex: number) {
        // Cant backspace at the start of the string
        if (caretIndex == 0) {
            return caretIndex;
        }
        // Since we are backspacing we want the maskItem for the previous caretposition since thats what we are gonna change
        caretIndex--;
        let maskItem = this.getMaskItemForCaretIndex(caretIndex);

        // If it's a separator, backspace should just skip across the separator
        if (maskItem instanceof InputMaskSeparator) {
            caretIndex = this.skipCaretAcrossSeparatorsBackwards(caretIndex);
        }

        maskItem = this.getMaskItemForCaretIndex(caretIndex);

        const relativeCharIndex = this.getRelativeCharIndex(caretIndex, maskItem);

        maskItem.removeUserInput(relativeCharIndex);

        return caretIndex;
    }

    private handleDelete(caretIndex: number) {
        for (let i = caretIndex; i < this.maskItemCaretIndexMap.length; i++) {
            const maskItem = this.getMaskItemForCaretIndex(i);

            let relativeCharIndex = 0;
            // For the first maskItem we have to get the relative index so we dont delete the first character if we are trying to delete the second character
            if (i == caretIndex) {
                relativeCharIndex = this.getRelativeCharIndex(caretIndex, maskItem);
            }

            // Try to delete every character in the mask until we delete something
            for (let i = relativeCharIndex; i < maskItem.mask.length; i++) {
                if (maskItem.removeUserInput(i)) {
                    // Skip across separators
                    if (maskItem instanceof InputMaskSeparator) {
                        continue;
                    }
                    return;
                }
            }
        }
    }

    private handleCharInput(char: string, caretIndex: number) {
        // If the next character is a separator, skip across the separator, so we put the input in the next maskItem
        if (this.getMaskItemForCaretIndex(caretIndex) instanceof InputMaskSeparator) {
            caretIndex = this.skipCaretAcrossSeparators(caretIndex);
        }

        const maskItem = this.getMaskItemForCaretIndex(caretIndex);

        const relativeCharIndex = this.getRelativeCharIndex(caretIndex, maskItem);

        // If the inputcharacter IS the mask character, we move the caretindex only one character simulating that we input the mask character
        // The justification for this is that if we have an unfilled mask the emitted value will include the mask character
        // and since this is bound we get the value with mask character included here
        // And we have to make sure we hit the correct character when we are refilling the mask
        if (maskItem.mask[relativeCharIndex] == char) {
            return ++caretIndex;
        }

        const changedSomething = maskItem.addUserInput(char, relativeCharIndex);

        // If nothing changed then the caretindex should stay the same
        if (!changedSomething) {
            return caretIndex;
        }

        // Move cursor
        caretIndex++;

        // If the next character is a separator, skip across the separator
        if (this.getMaskItemForCaretIndex(caretIndex) instanceof InputMaskSeparator) {
            caretIndex = this.skipCaretAcrossSeparators(caretIndex);
        }

        return caretIndex;
    }

    private skipCaretAcrossSeparatorsBackwards(caretIndex: number) {
        return this.skipCaretAcrossSeparators(caretIndex, true);
    }

    private skipCaretAcrossSeparators(caretIndex: number, backwards = false) {
        let factor = 1;
        if (backwards) {
            factor = -1;
        }
        let maskItem = this.getMaskItemForCaretIndex(caretIndex);
        while (maskItem instanceof InputMaskSeparator) {
            caretIndex += maskItem.mask.length * factor;

            maskItem.addUserInput('', 0);
            if (backwards) {
                maskItem.removeUserInput(this.getRelativeCharIndex(caretIndex, maskItem));
            }
            maskItem = this.getMaskItemForCaretIndex(caretIndex);
        }

        return caretIndex;
    }

    private getFullMask(): string {
        return this.MaskItems.reduce((res, mi) => res + mi.mask, '');
    }
}
